import { useState } from 'react'
import type { ShopDetail } from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { usePermission } from '../../hooks/usePermission'
import { FeeSchedulesPanel } from './FeeSchedulesPanel'
import { ImpersonateDialog } from './ImpersonateDialog'
import { ManagedListingForm } from './ManagedListingForm'
import { PlanPanel } from './PlanPanel'
import { BadgesPanel } from './BadgesPanel'
import { BanPanel } from './BanPanel'
import { cdnImage } from '../../utils/cdnImage'

type Tab = 'overview' | 'plan' | 'badges' | 'ban' | 'fees' | 'listing'

interface ShopDetailModalProps {
  shop: ShopDetail
  onClose: () => void
  onVerify: (notes?: string) => Promise<void>
  onSuspend: (reason: string) => Promise<void>
  onUnsuspend: (notes?: string) => Promise<void>
  onClose_: (reason: string) => Promise<void>
}

export function ShopDetailModal({
  shop,
  onClose,
  onVerify,
  onSuspend,
  onUnsuspend,
  onClose_,
}: ShopDetailModalProps) {
  const [action, setAction] = useState<'verify' | 'suspend' | 'close' | null>(null)
  const [reason, setReason] = useState('')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)
  const [tab, setTab] = useState<Tab>('overview')

  const canEdit = usePermission('shops', 'edit')
  const canDelete = usePermission('shops', 'delete')
  const canReadFees = usePermission('fee_schedules', 'read')
  const canCreateListingOnBehalf = usePermission('listings', 'create_on_behalf')
  const canImpersonate = usePermission('shops', 'impersonate')
  const [showImpersonate, setShowImpersonate] = useState<'web' | 'mobile' | null>(null)

  const handleAction = async () => {
    setLoading(true)
    try {
      if (action === 'verify') {
        await onVerify(notes || undefined)
      } else if (action === 'suspend') {
        await onSuspend(reason)
      } else if (action === 'close') {
        await onClose_(reason)
      }
      setAction(null)
      setReason('')
      setNotes('')
    } catch {
      // Error handled in parent
    } finally {
      setLoading(false)
    }
  }

  const handleUnsuspend = async () => {
    setLoading(true)
    try {
      await onUnsuspend()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white dark:bg-[#1a1520] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-6">
          <h2 className="font-heading text-xl font-bold text-gray-900 dark:text-gray-100">Shop Details</h2>
          <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="mb-6 flex border-b border-gray-200 dark:border-[#3d2c4f]">
          <button
            onClick={() => setTab('overview')}
            className={`px-4 py-2 text-sm font-medium ${
              tab === 'overview'
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 dark:text-gray-200'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setTab('plan')}
            className={`px-4 py-2 text-sm font-medium ${
              tab === 'plan'
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            Plan
          </button>
          <button
            onClick={() => setTab('badges')}
            className={`px-4 py-2 text-sm font-medium ${
              tab === 'badges'
                ? 'border-b-2 border-primary text-primary'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            Badges
          </button>
          {canEdit && (
            <button
              onClick={() => setTab('ban')}
              className={`px-4 py-2 text-sm font-medium ${
                tab === 'ban'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
              }`}
            >
              Ban
            </button>
          )}
          {canReadFees && (
            <button
              onClick={() => setTab('fees')}
              className={`px-4 py-2 text-sm font-medium ${
                tab === 'fees'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 dark:text-gray-200'
              }`}
            >
              Fees
            </button>
          )}
          {canCreateListingOnBehalf && (
            <button
              onClick={() => setTab('listing')}
              className={`px-4 py-2 text-sm font-medium ${
                tab === 'listing'
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 dark:text-gray-200'
              }`}
            >
              New Listing
            </button>
          )}
        </div>

        {tab === 'plan' && <PlanPanel shopId={shop.id} />}
        {tab === 'badges' && <BadgesPanel shopId={shop.id} />}
        {tab === 'ban' && <BanPanel shopId={shop.id} />}
        {tab === 'fees' && <FeeSchedulesPanel shopId={shop.id} />}
        {tab === 'listing' && <ManagedListingForm shopId={shop.id} />}

        {tab === 'overview' && (
          <>

        {/* Shop Info */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            {shop.logo_url ? (
              <img src={cdnImage(shop.logo_url, { w: 128, q: 80 }) ?? shop.logo_url} alt={shop.name} className="h-16 w-16 rounded-xl object-cover" />
            ) : (
              <div className="h-16 w-16 rounded-xl bg-primary/10 flex items-center justify-center">
                <span className="text-2xl font-bold text-primary">{shop.name.charAt(0)}</span>
              </div>
            )}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{shop.name}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">{shop.status}</p>
            </div>
          </div>

          {shop.description && (
            <p className="text-gray-600 dark:text-gray-300 mb-4">{shop.description}</p>
          )}

          <div className="grid grid-cols-2 gap-4 text-sm mb-4">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Owner:</span>
              <span className="ml-2">{shop.owner_name || shop.owner_email || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Location:</span>
              <span className="ml-2">{shop.location || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Shop ID:</span>
              <span className="ml-2 font-mono text-xs">{shop.id}</span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Slug:</span>
              <span className="ml-2 font-mono text-xs">{shop.slug_token || 'N/A'}</span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Listings:</span>
              <span className="ml-2">{shop.listing_count}</span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Total Sales:</span>
              <span className="ml-2">{shop.total_sales}</span>
            </div>
          </div>

          <div className="border-t border-gray-100 dark:border-[#3d2c4f] pt-4 text-sm text-gray-500 dark:text-gray-400">
            <p>Created: {new Date(shop.created_at).toLocaleDateString()}</p>
            {shop.verified_at && (
              <p>Verified: {new Date(shop.verified_at).toLocaleDateString()}</p>
            )}
          </div>
        </div>

        {/* Moderation Notes */}
        {shop.moderation_notes && (
          <div className="mb-6 rounded-xl bg-gray-50 dark:bg-[#2a1f35] p-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Moderation Notes</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">{shop.moderation_notes}</p>
          </div>
        )}

        {/* Action Form */}
        {action && (
          <div className="mb-6 rounded-xl border border-gray-200 dark:border-[#3d2c4f] p-4">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3 capitalize">{action} Shop</h4>
            {action === 'verify' ? (
              <CustomTextInput
                label="Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Verification notes..."
              />
            ) : (
              <CustomTextInput
                label="Reason (required)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason..."
              />
            )}
            <div className="mt-4 flex gap-2">
              <CustomButton
                onClick={handleAction}
                disabled={loading || (action !== 'verify' && !reason)}
                variant={action === 'verify' ? 'primary' : 'danger'}
              >
                {loading ? 'Processing...' : `Confirm ${action}`}
              </CustomButton>
              <CustomButton onClick={() => setAction(null)} variant="secondary">
                Cancel
              </CustomButton>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {!action && (
          <div className="flex flex-wrap gap-2">
            {shop.status === 'pending' && canEdit && (
              <CustomButton onClick={() => setAction('verify')} variant="primary">
                Verify Shop
              </CustomButton>
            )}
            {(shop.status === 'pending' || shop.status === 'verified') && canEdit && (
              <CustomButton onClick={() => setAction('suspend')} variant="secondary">
                Suspend
              </CustomButton>
            )}
            {shop.status === 'suspended' && canEdit && (
              <CustomButton onClick={handleUnsuspend} disabled={loading} variant="primary">
                Unsuspend
              </CustomButton>
            )}
            {shop.status !== 'closed' && canDelete && (
              <CustomButton onClick={() => setAction('close')} variant="danger">
                Close Shop
              </CustomButton>
            )}
            {canImpersonate && (
              <>
                <CustomButton onClick={() => setShowImpersonate('web')} variant="secondary">
                  Manage as Owner
                </CustomButton>
                <CustomButton onClick={() => setShowImpersonate('mobile')} variant="secondary">
                  Open in Mobile
                </CustomButton>
              </>
            )}
          </div>
        )}
          </>
        )}
        {showImpersonate && (
          <ImpersonateDialog
            shopId={shop.id}
            shopName={shop.name}
            ownerEmail={shop.owner_email}
            mode={showImpersonate}
            onClose={() => setShowImpersonate(null)}
          />
        )}
      </div>
    </div>
  )
}
